import { httpsCallable } from "firebase/functions";
import { functions } from "../FirebaseConfig";
import { apiLogger } from "../services/LoggingService";

/**
 * Wrappers around the eventsFn.js Cloud Functions (v2 Cadillac).
 *
 * Each callable matches the export name in functions/index.js:
 *   createEvent / updateEvent / cancelEvent / cancelEventOccurrence /
 *   listEvents / rsvpToEvent / removeEvent /
 *   linkRedeemableToEvent / unlinkRedeemableFromEvent
 */

export async function createEvent(input) {
  const fn = httpsCallable(functions, "createEvent");
  try {
    const res = await fn(input);
    apiLogger.trackAction("event_created", { eventId: res.data?.eventId, status: res.data?.status });
    return res.data;
  } catch (err) {
    apiLogger.error("Failed to create event", err);
    throw err;
  }
}

export async function updateEvent(eventId, updates) {
  const fn = httpsCallable(functions, "updateEvent");
  try {
    const res = await fn({ eventId, ...updates });
    apiLogger.trackAction("event_updated", { eventId, status: res.data?.status });
    return res.data;
  } catch (err) {
    apiLogger.error("Failed to update event", err, { eventId });
    throw err;
  }
}

export async function cancelEvent(eventId) {
  const fn = httpsCallable(functions, "cancelEvent");
  try {
    const res = await fn({ eventId });
    apiLogger.trackAction("event_cancelled", { eventId });
    return res.data;
  } catch (err) {
    apiLogger.error("Failed to cancel event", err, { eventId });
    throw err;
  }
}

export async function cancelEventOccurrence(eventId, occurrenceStartAt) {
  const fn = httpsCallable(functions, "cancelEventOccurrence");
  try {
    const res = await fn({ eventId, occurrenceStartAt });
    apiLogger.trackAction("event_occurrence_cancelled", { eventId, occurrenceStartAt });
    return res.data;
  } catch (err) {
    apiLogger.error("Failed to cancel occurrence", err, { eventId });
    throw err;
  }
}

export async function listEvents(filters = {}) {
  const fn = httpsCallable(functions, "listEvents");
  try {
    const res = await fn(filters);
    return res.data?.events || [];
  } catch (err) {
    apiLogger.error("Failed to list events", err, { filters });
    throw err;
  }
}

export async function rsvpToEvent(eventId) {
  const fn = httpsCallable(functions, "rsvpToEvent");
  try {
    const res = await fn({ eventId });
    apiLogger.trackAction("event_rsvp", { eventId });
    return res.data;
  } catch (err) {
    apiLogger.error("Failed to RSVP", err, { eventId });
    throw err;
  }
}

export async function removeEvent(eventId, reason = "") {
  const fn = httpsCallable(functions, "removeEvent");
  const res = await fn({ eventId, reason });
  apiLogger.trackAction("event_removed_by_admin", { eventId });
  return res.data;
}

export async function linkRedeemableToEvent(eventId, itemId) {
  const fn = httpsCallable(functions, "linkRedeemableToEvent");
  const res = await fn({ eventId, itemId });
  return res.data;
}

export async function unlinkRedeemableFromEvent(eventId, itemId) {
  const fn = httpsCallable(functions, "unlinkRedeemableFromEvent");
  const res = await fn({ eventId, itemId });
  return res.data;
}

// ---------------------------------------------------------------------------
// Owner-side RSVP listing
// ---------------------------------------------------------------------------
export async function listEventRsvps(eventId, opts = {}) {
  const fn = httpsCallable(functions, "listEventRsvps");
  try {
    const res = await fn({ eventId, ...opts });
    return res.data;
  } catch (err) {
    apiLogger.error("Failed to list event RSVPs", err, { eventId });
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Event analytics — fire-and-forget tracking callables.
// We intentionally swallow errors so a tracking failure never breaks the UX.
// ---------------------------------------------------------------------------
export async function trackEventView(eventId) {
  if (!eventId) return;
  try {
    // Per-session dedupe so a single page view counts once per session.
    const key = `event-view:${eventId}`;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(key)) return;
    const fn = httpsCallable(functions, "trackEventView");
    await fn({ eventId });
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(key, "1");
  } catch (_err) { /* silent */ }
}

export async function trackEventEngagement(eventId, action) {
  if (!eventId || !action) return;
  try {
    const fn = httpsCallable(functions, "trackEventEngagement");
    await fn({ eventId, action });
  } catch (_err) { /* silent */ }
}

